/**
 * Opinion, treaties, truces and subjects — everything a crown holds toward
 * another crown that is not, itself, a war.
 *
 * The shapes are in `diplotypes.ts`, the wars are in `war.ts`, and the turn and
 * the AI are in `diploturn.ts`. This file is the middle: the readers the rest of
 * the game asks its questions through, and the small mutators that are the only
 * legal way to change any of it.
 *
 * Two rules hold everywhere below.
 *
 * **Nothing here rolls dice.** Every function is a pure read or a deterministic
 * write. The rolls live in `diploturn.ts`, where they come off `w.rngCampaign`
 * like everything else in this layer.
 *
 * **The pair `(a, b)` is stored once.** Treaties and truces normalise to `a < b`
 * on the way in, so there is exactly one record per relationship and no way for
 * the two halves to disagree. Opinion is the exception and is deliberately
 * asymmetric: what you think of them and what they think of you are two
 * different numbers, which is most of what makes a diplomatic map interesting.
 */

import { confessionFor, confessionHostility } from '../../data/confessions.ts';
import { nationDef } from '../../data/nations.ts';
import { provinceDef } from '../../data/provinces.ts';
import { armyPoints } from './armies.ts';
import { dropAllPassage, hasPassage } from './diplopass.ts';
import { atWar, garrisonCap } from './state.ts';
import { tierOf, tierYield } from './holdings.ts';
import {
  MAX_ALLIES,
  MAX_OFFERS,
  OPINION_DRIFT,
  OPINION_MAX,
  OPINION_MIN,
  OP_ALLIANCE,
  OP_AT_WAR,
  OP_BASE,
  OP_BORDER,
  OP_COMMON_ENEMY,
  OP_CONFESSION_HOSTILITY,
  OP_MARRIAGE,
  OP_PASSAGE,
  OP_SAME_CONFESSION,
  OP_SUBJECT,
  OP_TRUCE,
  SubjectKind,
  TreatyKind,
  type DiploOffer,
  type Treaty,
} from './diplotypes.ts';
import type { CampaignState } from './types.ts';

// ── Confession ─────────────────────────────────────────────────────────────

/**
 * The faith a nation is *born* with, from the data table.
 *
 * What it currently keeps is `Nation.confession`, which is state and can move.
 * This is only the opening position, and it is what `createCampaign` and the
 * save loader both fall back to.
 */
export function startingConfession(nationDefIdx: number): number {
  const def = nationDef(nationDefIdx);
  return confessionFor(def.factionId, def.id);
}

export function confessionOf(c: CampaignState, n: number): number {
  return c.nations[n]?.confession ?? 0;
}

export function sameConfession(c: CampaignState, a: number, b: number): boolean {
  return confessionOf(c, a) === confessionOf(c, b);
}

/** 0..1 — how badly two crowns' faiths get on. Zero when they share one. */
export function faithHostility(c: CampaignState, a: number, b: number): number {
  return confessionHostility(confessionOf(c, a), confessionOf(c, b));
}

// ── Opinion ────────────────────────────────────────────────────────────────

/** What `a` currently thinks of `b`. -100..100. */
export function opinion(c: CampaignState, a: number, b: number): number {
  if (a === b) return OPINION_MAX;
  return c.nations[a]?.relations[b] ?? 0;
}

export function addOpinion(c: CampaignState, a: number, b: number, delta: number): void {
  const n = c.nations[a];
  if (!n || a === b || b < 0 || b >= n.relations.length) return;
  n.relations[b] = clampOpinion(n.relations[b] + delta);
}

/** The usual case: an injury is felt by both parties, in both directions. */
export function addOpinionBoth(c: CampaignState, a: number, b: number, delta: number): void {
  addOpinion(c, a, b, delta);
  addOpinion(c, b, a, delta);
}

/**
 * What the realm as a whole makes of something one crown did.
 *
 * Linear in the number of nations, called once per event rather than per turn,
 * and it is the whole of this system's answer to EU4's aggressive expansion and
 * coalitions. A crown that eats its neighbours becomes universally disliked;
 * dislike is what stops anybody allying with it and what makes everybody else's
 * war-declaration arithmetic point at it. That produces coalitions as a *result*
 * rather than as a mechanic with its own screen — which is the trade, and it is
 * worth saying that the thing lost is the coalition war itself. Nobody declares
 * on you all at once. They simply, separately, all want to.
 */
export function addOpinionRealm(
  c: CampaignState,
  about: number,
  delta: number,
  except: readonly number[] = [],
): void {
  for (let n = 0; n < c.nations.length; n++) {
    if (n === about || !c.nations[n].alive) continue;
    if (except.includes(n)) continue;
    addOpinion(c, n, about, delta);
  }
}

/**
 * Clamp, and beat negative zero out of the result.
 *
 * `-0` is a different float from `0` — different bits, and `hashWorld` hashes the
 * bits, which is the entire point of hashing them. `JSON.stringify` writes it as
 * `0`. So an opinion that lands on `-0` hashes one way before a save and another
 * way after it, and a campaign that round-tripped perfectly in every field
 * reported a different state hash.
 *
 * That is not hypothetical. The default hostility between two faiths is 0.35 and
 * the confession term is -35, so the opening opinion of most pairs on the board
 * is `Math.round(12 - 12.25)` — which is `-0`, on every crown, in every campaign.
 * The save test caught it; nothing else would have.
 */
function clampOpinion(v: number): number {
  const clamped = Math.max(OPINION_MIN, Math.min(OPINION_MAX, v));
  return clamped === 0 ? 0 : clamped;
}

/**
 * Where a relationship settles if nothing else ever happens.
 *
 * Read off the board — faith, treaties, subjection, war, a shared border — and
 * therefore always answerable to the player with "look at the map". The panel
 * prints the terms individually; `opinionTerms` is the same sum, itemised.
 */
export function baseOpinion(c: CampaignState, a: number, b: number): number {
  if (a === b) return OPINION_MAX;
  let v = OP_BASE;
  const hostility = faithHostility(c, a, b);
  if (hostility === 0) v += OP_SAME_CONFESSION;
  else v += OP_CONFESSION_HOSTILITY * hostility;
  if (isAllied(c, a, b)) v += OP_ALLIANCE;
  if (hasMarriage(c, a, b)) v += OP_MARRIAGE;
  if (isSubjectOf(c, a, b) || isSubjectOf(c, b, a)) v += OP_SUBJECT;
  // A standing right of passage either way. Two countries whose armies are
  // allowed to cross one another have an arrangement, and an arrangement is
  // worth a few points against the border penalty that is almost certainly also
  // in this sum. It is not friendship: a crown that granted out of fear does not
  // thereby like the crown it was frightened of, which is why it is small.
  if (hasPassage(c, a, b) || hasPassage(c, b, a)) v += OP_PASSAGE;
  if (truceBetween(c, a, b)) v += OP_TRUCE;
  if (atWar(c, a, b)) v += OP_AT_WAR;
  if (bordersNation(c, a, b)) v += OP_BORDER;
  if (sharesEnemy(c, a, b)) v += OP_COMMON_ENEMY;
  return clampOpinion(v);
}

/**
 * Both of them are fighting somebody, and it is the same somebody.
 *
 * Read straight off the two `atWar` rows, which is a scan of the nation count
 * per pair — and would therefore make the opinion drift cubic if it ran for every
 * pair. It does not: the first line returns immediately when there is no war
 * anywhere on the board, and the loop only runs for pairs where both crowns have
 * something to be at war about. In a realm of thirty at peace it costs nothing; in
 * a realm of thirty with five wars running it costs a few thousand comparisons a
 * month of game time, which was measured before it was written and is not worth
 * a cleverer structure.
 */
export function sharesEnemy(c: CampaignState, a: number, b: number): boolean {
  if (c.diplo.wars.length === 0) return false;
  const ra = c.nations[a]?.atWar;
  const rb = c.nations[b]?.atWar;
  if (!ra || !rb) return false;
  for (let x = 0; x < ra.length; x++) {
    if (x === a || x === b) continue;
    if (ra[x] === 1 && rb[x] === 1) return true;
  }
  return false;
}

/** The same sum, itemised, for the diplomacy panel. */
export function opinionTerms(
  c: CampaignState,
  a: number,
  b: number,
): Array<{ label: string; value: number }> {
  const out: Array<{ label: string; value: number }> = [{ label: 'No quarrel', value: OP_BASE }];
  const hostility = faithHostility(c, a, b);
  if (hostility === 0) out.push({ label: 'Same confession', value: OP_SAME_CONFESSION });
  else out.push({ label: 'Different confession', value: Math.round(OP_CONFESSION_HOSTILITY * hostility) });
  if (isAllied(c, a, b)) out.push({ label: 'Allied', value: OP_ALLIANCE });
  if (hasMarriage(c, a, b)) out.push({ label: 'Royal marriage', value: OP_MARRIAGE });
  if (isSubjectOf(c, a, b) || isSubjectOf(c, b, a)) out.push({ label: 'Sworn', value: OP_SUBJECT });
  if (hasPassage(c, a, b) || hasPassage(c, b, a)) {
    out.push({ label: 'Right of passage', value: OP_PASSAGE });
  }
  if (truceBetween(c, a, b)) out.push({ label: 'Truce', value: OP_TRUCE });
  if (atWar(c, a, b)) out.push({ label: 'At war', value: OP_AT_WAR });
  if (bordersNation(c, a, b)) out.push({ label: 'Shared border', value: OP_BORDER });
  if (sharesEnemy(c, a, b)) out.push({ label: 'Enemy in common', value: OP_COMMON_ENEMY });
  return out;
}

/**
 * Move every opinion one step toward where the board says it belongs.
 *
 * This is the one genuinely `n²` thing in the system, and it is `n²` per *turn*
 * rather than per tick: at thirty kingdoms it is nine hundred comparisons a
 * month of game time, against a turn that already runs a breadth-first search of
 * the province graph for every army on the board. It was worth measuring before
 * writing something cleverer, and having measured it, nothing cleverer was
 * written. If the realm ever reaches a size where this matters, the fix is to
 * walk only pairs whose stored value differs from their base, which is a
 * one-line change and costs nothing today.
 */
export function driftOpinions(c: CampaignState): void {
  for (let a = 0; a < c.nations.length; a++) {
    const na = c.nations[a];
    if (!na.alive) continue;
    for (let b = 0; b < c.nations.length; b++) {
      if (a === b || !c.nations[b].alive) continue;
      const base = baseOpinion(c, a, b);
      const now = na.relations[b] ?? 0;
      if (now === base) continue;
      const step = Math.min(OPINION_DRIFT, Math.abs(base - now));
      na.relations[b] = clampOpinion(now + (base > now ? step : -step));
    }
  }
}

// ── The nation graph ───────────────────────────────────────────────────────

/**
 * Which crowns share a border with which, as a set per nation.
 *
 * Built in one pass over the province graph — linear in edges, not in nations
 * squared — and rebuilt whenever it is asked for rather than cached, because a
 * border moves every time a province changes hands and a stale border list would
 * be a cache that changes what the AI does. The council builds it once a turn and
 * hands it round.
 */
export function neighbourNations(c: CampaignState): Set<number>[] {
  const out: Set<number>[] = c.nations.map(() => new Set<number>());
  for (const l of c.links) {
    const oa = c.provinces[l.a]?.owner ?? -1;
    const ob = c.provinces[l.b]?.owner ?? -1;
    if (oa < 0 || ob < 0 || oa === ob) continue;
    out[oa].add(ob);
    out[ob].add(oa);
  }
  for (const l of c.lanes) {
    const oa = c.provinces[l.a]?.owner ?? -1;
    const ob = c.provinces[l.b]?.owner ?? -1;
    if (oa < 0 || ob < 0 || oa === ob) continue;
    out[oa].add(ob);
    out[ob].add(oa);
  }
  return out;
}

/**
 * Whether two crowns hold ground that touches — **by road or by lane**.
 *
 * The lanes are in here, and adding them was the single change that made the
 * sea matter to anybody but the player.
 *
 * This walked `c.links` alone, which was exactly right while the realm was one
 * walkable continent: everybody's neighbours were the people over the hill.
 * On a world of seven reaches around a Midsea it silently ended the game. Every
 * step of diplomacy is bounded by this graph — who may be declared on, who is a
 * candidate for an alliance, whose opinion moves — so two crowns facing each
 * other across a channel were not neighbours, could never go to war, and
 * therefore `mayAttack` refused every landing there was. Measured: twenty-two
 * harbours and a hundred and fifty-six hulls per campaign, and one and a third
 * fleet-months at sea in ten years. Everybody built a navy and nobody ever had
 * anywhere to point it.
 *
 * A lane is a border. That is not a convenience — it is the claim the whole
 * world is built on. Two powers on opposite shores of a narrow channel are far
 * more each other's problem than two powers a fortnight's march apart on the
 * same continent, and the Midsea is a highway rather than a wall for exactly
 * the crowns that have hulls on it.
 */
export function bordersNation(c: CampaignState, a: number, b: number): boolean {
  const touches = (x: number, y: number): boolean =>
    (x === a && y === b) || (x === b && y === a);
  for (const l of c.links) {
    if (touches(c.provinces[l.a]?.owner ?? -1, c.provinces[l.b]?.owner ?? -1)) return true;
  }
  for (const l of c.lanes) {
    if (touches(c.provinces[l.a]?.owner ?? -1, c.provinces[l.b]?.owner ?? -1)) return true;
  }
  return false;
}

// ── Treaties ───────────────────────────────────────────────────────────────

function pair(a: number, b: number): [number, number] {
  return a < b ? [a, b] : [b, a];
}

export function treatyBetween(
  c: CampaignState,
  kind: number,
  a: number,
  b: number,
): Treaty | undefined {
  const [lo, hi] = pair(a, b);
  return c.diplo.treaties.find((t) => t.kind === kind && t.a === lo && t.b === hi);
}

export function isAllied(c: CampaignState, a: number, b: number): boolean {
  if (a === b) return false;
  return treatyBetween(c, TreatyKind.Alliance, a, b) !== undefined;
}

export function hasMarriage(c: CampaignState, a: number, b: number): boolean {
  if (a === b) return false;
  return treatyBetween(c, TreatyKind.RoyalMarriage, a, b) !== undefined;
}

/** Everyone this crown is allied to. Includes nobody it merely likes. */
export function alliesOf(c: CampaignState, n: number): number[] {
  const out: number[] = [];
  for (const t of c.diplo.treaties) {
    if (t.kind !== TreatyKind.Alliance) continue;
    if (t.a === n) out.push(t.b);
    else if (t.b === n) out.push(t.a);
  }
  return out.filter((x) => c.nations[x]?.alive).sort((x, y) => x - y);
}

export function marriagesOf(c: CampaignState, n: number): Treaty[] {
  return c.diplo.treaties.filter(
    (t) => t.kind === TreatyKind.RoyalMarriage && (t.a === n || t.b === n),
  );
}

export function addTreaty(c: CampaignState, kind: number, a: number, b: number): boolean {
  if (a === b || a < 0 || b < 0) return false;
  if (!c.nations[a]?.alive || !c.nations[b]?.alive) return false;
  if (treatyBetween(c, kind, a, b)) return false;
  if (kind === TreatyKind.Alliance) {
    if (alliesOf(c, a).length >= MAX_ALLIES) return false;
    if (alliesOf(c, b).length >= MAX_ALLIES) return false;
  }
  const [lo, hi] = pair(a, b);
  c.diplo.treaties.push({ kind, a: lo, b: hi, since: c.turn });
  sortTreaties(c);
  return true;
}

export function removeTreaty(c: CampaignState, kind: number, a: number, b: number): boolean {
  const [lo, hi] = pair(a, b);
  const i = c.diplo.treaties.findIndex((t) => t.kind === kind && t.a === lo && t.b === hi);
  if (i < 0) return false;
  c.diplo.treaties.splice(i, 1);
  return true;
}

/**
 * Drop every treaty either party holds with anybody. Used when a crown dies.
 *
 * The grants of passage go with them, both directions. A dead crown's leave to
 * cross ground it no longer holds is meaningless, and leave *it* gave would
 * otherwise sit in the list forever holding a door open onto provinces that have
 * changed hands.
 */
export function dropAllTreaties(c: CampaignState, n: number): void {
  c.diplo.treaties = c.diplo.treaties.filter((t) => t.a !== n && t.b !== n);
  dropAllPassage(c, n);
}

/**
 * Ordered so that saving, hashing and iteration are all independent of the order
 * things happened in. A campaign that signed the same two treaties in the other
 * order must hash the same.
 */
function sortTreaties(c: CampaignState): void {
  c.diplo.treaties.sort((x, y) => x.kind - y.kind || x.a - y.a || x.b - y.b);
}

// ── Truces ─────────────────────────────────────────────────────────────────

export function truceBetween(c: CampaignState, a: number, b: number): boolean {
  return truceTurnsLeft(c, a, b) > 0;
}

export function truceTurnsLeft(c: CampaignState, a: number, b: number): number {
  const [lo, hi] = pair(a, b);
  const t = c.diplo.truces.find((x) => x.a === lo && x.b === hi);
  return t ? Math.max(0, t.until - c.turn) : 0;
}

/** Set, or extend. A second peace never shortens a truce that already stands. */
export function setTruce(c: CampaignState, a: number, b: number, turns: number): void {
  if (a === b || turns <= 0) return;
  const [lo, hi] = pair(a, b);
  const until = c.turn + Math.round(turns);
  const existing = c.diplo.truces.find((x) => x.a === lo && x.b === hi);
  if (existing) {
    existing.until = Math.max(existing.until, until);
    return;
  }
  c.diplo.truces.push({ a: lo, b: hi, until });
  c.diplo.truces.sort((x, y) => x.a - y.a || x.b - y.b);
}

export function expireTruces(c: CampaignState): void {
  c.diplo.truces = c.diplo.truces.filter((t) => t.until > c.turn);
}

// ── Subjects ───────────────────────────────────────────────────────────────

export function liegeOf(c: CampaignState, n: number): number {
  return c.nations[n]?.liege ?? -1;
}

export function isSubjectOf(c: CampaignState, subject: number, liege: number): boolean {
  if (subject < 0 || liege < 0) return false;
  return c.nations[subject]?.liege === liege;
}

export function subjectsOf(c: CampaignState, liege: number): number[] {
  const out: number[] = [];
  for (let n = 0; n < c.nations.length; n++) {
    if (c.nations[n].alive && c.nations[n].liege === liege) out.push(n);
  }
  return out;
}

/**
 * The crown at the top of the chain above `n`, or `n` itself.
 *
 * The walk itself lives in `diplopass.ts` as `topCrown`, because march legality
 * needs it and `diplopass.ts` is imported by everything that moves — it cannot
 * import this file without closing a cycle. Two copies of a guarded loop over
 * `liege` is exactly the kind of duplication that drifts, so there is one, and
 * these two names are the doors the rest of the system already knocks on.
 */
export { topCrown as topLiege, sameCrown as sameRealm } from './diplopass.ts';

export function setSubject(c: CampaignState, subject: number, liege: number, kind: number): void {
  const n = c.nations[subject];
  if (!n) return;
  n.liege = liege;
  n.subject = kind;
  n.subjectSince = c.turn;
  // A crown that has bent the knee is not also somebody's ally: the alliance was
  // between equals and there are no longer two of those.
  removeTreaty(c, TreatyKind.Alliance, subject, liege);
}

export function freeSubject(c: CampaignState, subject: number): void {
  const n = c.nations[subject];
  if (!n) return;
  n.liege = -1;
  n.subject = SubjectKind.None;
  n.subjectSince = 0;
}

// ── Offers ─────────────────────────────────────────────────────────────────

export function addOffer(c: CampaignState, offer: Omit<DiploOffer, 'id'>): DiploOffer | null {
  if (c.diplo.offers.length >= MAX_OFFERS) return null;
  const full: DiploOffer = { ...offer, id: c.diplo.nextOfferId++ };
  c.diplo.offers.push(full);
  return full;
}

export function findOffer(c: CampaignState, id: number): DiploOffer | undefined {
  return c.diplo.offers.find((o) => o.id === id);
}

export function removeOffer(c: CampaignState, id: number): void {
  const i = c.diplo.offers.findIndex((o) => o.id === id);
  if (i >= 0) c.diplo.offers.splice(i, 1);
}

/** Everything currently sitting on one crown's table, soonest to lapse first. */
export function offersFor(c: CampaignState, nation: number): DiploOffer[] {
  return c.diplo.offers.filter((o) => o.to === nation).sort((a, b) => a.expires - b.expires || a.id - b.id);
}

export function hasOfferFrom(c: CampaignState, kind: number, from: number, to: number): boolean {
  return c.diplo.offers.some((o) => o.kind === kind && o.from === from && o.to === to);
}

// ── How big a crown is ─────────────────────────────────────────────────────

/**
 * One number for "how much power is that", used everywhere the AI has to decide
 * whether it can afford a war.
 *
 * Field armies, plus what the ground itself is worth defending with. A crown's
 * hosts are the part that can be aimed; its garrisons are the part that has to be
 * dug out. Counting only armies makes a walled, garrisoned kingdom look like an
 * easy mark, and the first version of this AI declared on exactly that kingdom
 * every time.
 */
export function nationStrength(c: CampaignState, n: number): number {
  let total = 0;
  for (const a of c.armies) if (a.owner === n) total += armyPoints(a);
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== n) continue;
    total += c.provinces[p].garrison * 0.6 + c.provinces[p].fort * 30;
  }
  // Subjects fight for their liege, so they count toward it.
  for (const s of subjectsOf(c, n)) {
    for (const a of c.armies) if (a.owner === s) total += armyPoints(a) * 0.8;
  }
  return total;
}

/** Everything a crown would bring, allies included. What a declaration weighs. */
export function coalitionStrength(c: CampaignState, n: number): number {
  let total = nationStrength(c, n);
  for (const ally of alliesOf(c, n)) total += nationStrength(c, ally) * 0.7;
  return total;
}

export function provincesHeld(c: CampaignState, n: number): number {
  let held = 0;
  for (const p of c.provinces) if (p.owner === n) held++;
  return held;
}

/**
 * What a province is worth in a negotiation.
 *
 * Not the same number `ai.ts` uses to decide where to march — that one is about
 * whether a host can take it, and folds in the garrison standing on it. This one
 * is about what it is worth *owning*, which is why walls count for less here than
 * they do there: a peace hands the walls over intact, so they are a prize rather
 * than an obstacle.
 */
export function provinceWorth(c: CampaignState, p: number): number {
  const def = provinceDef(p);
  const prov = c.provinces[p];
  // The ladder counts double what the walls do, and it should: a peace hands a
  // great city over with its yard, its market and its roads intact, which is the
  // single largest thing that can change hands at a table.
  return (
    def.wealth * tierYield(tierOf(c, p)) +
    prov.fort * 4 +
    (prov.development - 1) * 12 +
    (tierOf(c, p) - 1) * 8 +
    garrisonCap(c, p) / 40
  );
}
